import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Observable, of, mergeMap, map } from 'rxjs';
import { environment } from '@env/environment';
import { AuthService } from '@core/services/auth/auth.service';

@Injectable()
export class CorrelationInterceptor implements HttpInterceptor {
    constructor(private authService: AuthService, private ngZone: NgZone) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const mutationMethods = ['POST', 'PATCH', 'DELETE', 'PUT'];
        if (!mutationMethods.includes(req.method)) {
            return next.handle(req);
        }

        return next.handle(req).pipe(
            mergeMap(event => {
                if (!(event instanceof HttpResponse)) return of(event);

                const body = event.body;
                const correlationId =
                    body?.correlation_id ||
                    body?.data?.correlation_id;

                if (!correlationId) return of(event);
                if (this.isSignupRequest(req.url)) return of(event);

                const token = this.authService.getToken().trim();
                if (!token) return of(event);

                return this.waitForWebSocketResult(correlationId, token).pipe(
                    map(wsData => event.clone({ body: wsData }))
                );
            })
        );
    }

    private waitForWebSocketResult(correlationId: string, token: string): Observable<any> {
        return new Observable(observer => {
            const wsUrl = this.buildWebSocketUrl(correlationId, token);
            const ws = new WebSocket(wsUrl);
            let settled = false;

            const settle = (fn: () => void) => {
                if (settled) return;
                settled = true;
                clearTimeout(timeout);
                this.ngZone.run(fn);
            };

            const timeout = setTimeout(() => {
                settle(() => {
                    ws.close();
                    observer.error(new HttpErrorResponse({
                        error: `WebSocket timeout for correlation_id: ${correlationId}`,
                        status: 504,
                        statusText: 'Gateway Timeout',
                    }));
                });
            }, 60000);

            ws.onmessage = (event: MessageEvent) => {
                this.ngZone.run(() => {
                    try {
                        const parsed = JSON.parse(event.data);
                        const data = parsed?.data ?? parsed;

                        const errorPayload = data?.error ?? data?.payload?.error;
                        const isFailed = data?.status === 'FAILED' || !!errorPayload;
                        if (isFailed) {
                            const errorMessage = typeof errorPayload === 'string'
                                ? errorPayload
                                : JSON.stringify(errorPayload ?? data);
                            settle(() => {
                                ws.close();
                                observer.error(new HttpErrorResponse({
                                    error: data,
                                    status: data.status_code ?? data.code ?? 500,
                                    statusText: errorMessage,
                                }));
                            });
                        } else {
                            settle(() => {
                                ws.close();
                                observer.next(data);
                                observer.complete();
                            });
                        }
                    } catch {
                        settle(() => {
                            ws.close();
                            observer.error(new HttpErrorResponse({
                                error: 'Invalid WebSocket message format',
                                status: 0,
                                statusText: 'Invalid WebSocket message format',
                            }));
                        });
                    }
                });
            };

            ws.onclose = () => {
                // WS closed without a message: the HTTP 200 already confirmed the operation
                // (backend may process via Kafka without sending WS confirmation for some endpoints)
                settle(() => {
                    observer.next(null);
                    observer.complete();
                });
            };

            ws.onerror = () => {
                settle(() => {
                    observer.error(new HttpErrorResponse({
                        error: 'WebSocket connection error',
                        status: 0,
                        statusText: 'WebSocket connection error',
                    }));
                });
            };

            return () => {
                settled = true;
                clearTimeout(timeout);
                if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                    ws.close();
                }
            };
        });
    }

    private isSignupRequest(url: string): boolean {
        return /\/signup(?:\/|$|\?)/i.test(url);
    }

    private buildWebSocketUrl(correlationId: string, token: string): string {
        const baseUrl = environment.url_ws_events_api || '';

        if (baseUrl.startsWith('ws://') || baseUrl.startsWith('wss://'))
            return `${baseUrl.replace(/\/$/, '')}/ws/${correlationId}?token=${token}`;
        if (baseUrl.startsWith('https://'))
            return `${baseUrl.replace('https://', 'wss://').replace(/\/$/, '')}/ws/${correlationId}?token=${token}`;
        if (baseUrl.startsWith('http://'))
            return `${baseUrl.replace('http://', 'ws://').replace(/\/$/, '')}/ws/${correlationId}?token=${token}`;

        return `ws://${baseUrl.replace(/\/$/, '')}/ws/${correlationId}`;
    }
}
